import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { Button } from "@excalidraw-ms/shared-ui";
import { dialogTitleStyle } from "./destructiveStyles";

export const MAX_DRAWING_TITLE_LENGTH = 120;

interface RenameDrawingDialogProps {
  currentTitle: string;
  renaming: boolean;
  error?: string | null;
  onRename: (name: string) => void;
  onClose: () => void;
}

/**
 * Renames a drawing from its gallery card. Dismissible through an outside
 * click or Escape, so it carries no footer Cancel (ux-rules §15b and the
 * gallery Rename rule).
 */
export default function RenameDrawingDialog({
  currentTitle,
  renaming,
  error,
  onRename,
  onClose,
}: RenameDrawingDialogProps) {
  const [name, setName] = useState(currentTitle);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const close = useCallback(() => {
    if (!renaming) onClose();
  }, [onClose, renaming]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close]);

  const trimmed = name.trim();
  const canSubmit = !renaming && trimmed.length > 0 && trimmed !== currentTitle.trim();

  return (
    <div style={overlayStyle} onClick={close} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-drawing-title"
        style={dialogStyle}
        data-testid="rename-drawing-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="rename-drawing-title" style={dialogTitleStyle}>
          Rename drawing
        </h2>
        <form
          style={formStyle}
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) onRename(trimmed);
          }}
        >
          <div style={rowStyle}>
            <input
              ref={inputRef}
              aria-label="Drawing title"
              value={name}
              maxLength={MAX_DRAWING_TITLE_LENGTH}
              disabled={renaming}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? "rename-drawing-error" : undefined}
              onChange={(event) => setName(event.target.value)}
              style={inputStyle}
            />
            <div style={footerStyle}>
              {error && (
                <p id="rename-drawing-error" role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="dialog"
                disabled={!canSubmit}
                style={{ flexShrink: 0, marginLeft: "auto" }}
              >
                {renaming ? "Renaming…" : "Rename"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10001,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 24,
  fontFamily: EXCALIDRAW_FONT,
};

const formStyle: React.CSSProperties = { marginTop: 20 };

const rowStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 12 };

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--island-bg-color)",
  color: "inherit",
  fontSize: 15,
  fontFamily: "inherit",
};

const footerStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12 };

const errorStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: "var(--color-danger)",
};
